import axios from 'axios';
import { env } from '@/lib/env';
import { useAuthStore } from '@/stores/auth.store';

export const apiClient = axios.create({
  baseURL: env.VITE_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Inject access token on every request
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

// Transparent token refresh on 401
let isRefreshing = false;
let queue: Array<(token: string) => void> = [];

apiClient.interceptors.response.use(
  (res) => res,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const original = error.config as typeof error.config & { _retried?: boolean };
    const status = error.response?.status;
    const isAuthUrl = original?.url?.startsWith('/auth/');

    if (status === 401 && !isAuthUrl && !original?._retried && original) {
      original._retried = true;

      if (isRefreshing) {
        return new Promise((resolve) => {
          queue.push((token) => {
            original.headers['Authorization'] = `Bearer ${token}`;
            resolve(apiClient(original));
          });
        });
      }

      isRefreshing = true;
      const { refreshToken, setAuth, clearAuth, user } = useAuthStore.getState();

      try {
        const res = await axios.post<{ data: { accessToken: string; refreshToken: string } }>(
          `${env.VITE_API_URL}/auth/refresh`,
          { refreshToken },
        );
        const { accessToken: newAccess, refreshToken: newRefresh } = res.data.data;
        setAuth(user!, newAccess, newRefresh);
        queue.forEach((cb) => cb(newAccess));
        queue = [];
        original.headers['Authorization'] = `Bearer ${newAccess}`;
        return apiClient(original);
      } catch {
        clearAuth();
        window.location.href = '/login';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
